import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  const lastUpdated = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="bg-light min-vh-100">
      <section className="py-4">
        <Container fluid>
          <Row>
            <Col lg={12}>
              <div className="bg-white shadow-sm p-4 mb-4">
                <h1 className="h2 fw-bold mb-3 text-primary">Terms of Service</h1>
                <p className="text-muted d-flex align-items-center mb-3">
                  <i className="fas fa-clock me-2"></i>
                  Last updated: {lastUpdated}
                </p>
                <div className="border-top mb-3"></div>
                <p className="lead text-secondary mb-0">
                  These Terms of Service govern your use of Telvix's Fiber-To-The-Home (FTTH) internet services. 
                  Please read these terms carefully as they contain important information about your rights and obligations.
                </p>
              </div>

              <div className="bg-white shadow-sm p-4">
                <section className="mb-4">
                  <h2 className="h3 fw-bold text-primary mb-3">1. Service Description</h2>
                  <div className="bg-light p-3">
                    <p className="mb-3">Our FTTH service includes:</p>
                    <ul className="list-unstyled mb-0">
                      {[
                        'High-speed fiber optic internet connectivity',
                        'Installation of necessary fiber optic equipment',
                        'Network maintenance and technical support',
                        'Access to customer portal and account management',
                        'Optional equipment rental (routers, mesh systems)'
                      ].map((item, index) => (
                        <li key={index} className="mb-2 d-flex align-items-start">
                          <i className="fas fa-check-circle text-success me-2 mt-1"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-4">
                  <h2 className="h3 fw-bold text-primary mb-3">2. Installation and Equipment</h2>
                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="bg-light p-3 h-100">
                        <h3 className="h5 fw-bold mb-3">Installation Process</h3>
                        <ul className="list-unstyled mb-0">
                          {[
                            'Professional installation by certified technicians',
                            'Fiber cable routing and ONT placement',
                            'Equipment testing and configuration',
                            'Wi-Fi setup and optimization',
                            'Customer orientation and training'
                          ].map((item, index) => (
                            <li key={index} className="mb-2 d-flex align-items-start">
                              <i className="fas fa-tools text-primary me-2 mt-1"></i>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="bg-light p-3 h-100">
                        <h3 className="h5 fw-bold mb-3">Equipment Terms</h3>
                        <ul className="list-unstyled mb-0">
                          {[
                            'ONT remains property of Telvix',
                            'Equipment must be returned upon service termination',
                            'Proper care and maintenance required',
                            'Damage protection options available',
                            'Replacement terms for defective equipment'
                          ].map((item, index) => (
                            <li key={index} className="mb-2 d-flex align-items-start">
                              <i className="fas fa-box text-primary me-2 mt-1"></i>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="mb-4">
                  <h2 className="h3 fw-bold text-primary mb-3">3. Service Terms</h2>
                  <div className="row g-3">
                    {[
                      {
                        icon: 'tachometer-alt',
                        title: 'Speed Expectations',
                        desc: 'Actual speeds may vary based on network conditions and equipment capabilities'
                      },
                      {
                        icon: 'clock',
                        title: 'Service Availability',
                        desc: '99.9% uptime guarantee with scheduled maintenance windows'
                      },
                      {
                        icon: 'wifi',
                        title: 'Network Management',
                        desc: 'Fair usage policies and traffic management practices apply'
                      },
                      {
                        icon: 'shield-alt',
                        title: 'Security Measures',
                        desc: 'Network security protocols and monitoring systems in place'
                      }
                    ].map((item, index) => (
                      <div key={index} className="col-md-6">
                        <div className="d-flex bg-light p-3">
                          <div className="flex-shrink-0">
                            <i className={`fas fa-${item.icon} text-primary fa-lg`}></i>
                          </div>
                          <div className="flex-grow-1 ms-3">
                            <h3 className="h6 fw-bold mb-1">{item.title}</h3>
                            <p className="small text-secondary mb-0">{item.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="mb-4">
                  <h2 className="h3 fw-bold text-primary mb-3">4. Service Plans and Billing</h2>
                  <div className="bg-light p-3">
                    <div className="row g-4">
                      <div className="col-md-6">
                        <h3 className="h5 fw-bold mb-3">Payment Terms</h3>
                        <ul className="list-unstyled mb-0">
                          {[
                            'Monthly billing in advance',
                            'Auto-payment options available',
                            'Late payment penalties apply',
                            'Service suspension for non-payment',
                            'Transparent fee structure'
                          ].map((item, index) => (
                            <li key={index} className="mb-2 d-flex align-items-start">
                              <i className="fas fa-dollar-sign text-primary me-2 mt-1"></i>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="col-md-6">
                        <h3 className="h5 fw-bold mb-3">Plan Changes</h3>
                        <ul className="list-unstyled mb-0">
                          {[
                            'Upgrade at any time',
                            'Downgrade after minimum term',
                            'Pro-rated billing adjustments',
                            'No hidden fees',
                            'Clear change notification'
                          ].map((item, index) => (
                            <li key={index} className="mb-2 d-flex align-items-start">
                              <i className="fas fa-exchange-alt text-primary me-2 mt-1"></i>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="mb-4">
                  <h2 className="h3 fw-bold text-primary mb-3">5. Service Cancellation</h2>
                  <div className="bg-light p-3">
                    <div className="row g-4">
                      {[
                        {
                          title: 'Notice Period',
                          desc: '30 days written notice required for service cancellation'
                        },
                        {
                          title: 'Equipment Return',
                          desc: 'All Telvix equipment must be returned within 14 days'
                        },
                        {
                          title: 'Final Bill',
                          desc: 'Pro-rated charges and any outstanding amounts'
                        },
                        {
                          title: 'Account Closure',
                          desc: 'Confirmation of account closure will be provided'
                        }
                      ].map((item, index) => (
                        <div key={index} className="col-md-6">
                          <h4 className="h6 fw-bold text-primary mb-1">{item.title}</h4>
                          <p className="small text-secondary mb-0">{item.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>

                <section className="mb-4">
                  <h2 className="h3 fw-bold text-primary mb-3">6. Customer Support</h2>
                  <div className="bg-light p-3">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="d-flex align-items-start">
                          <i className="fas fa-headset text-primary me-2 mt-1"></i>
                          <div>
                            <h4 className="h6 fw-bold mb-1">24/7 Technical Support</h4>
                            <p className="small mb-0">1-800-TELVIX-SUPPORT<br />support@telvix.com</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="d-flex align-items-start">
                          <i className="fas fa-comments text-primary me-2 mt-1"></i>
                          <div>
                            <h4 className="h6 fw-bold mb-1">Customer Service</h4>
                            <p className="small mb-0">Mon-Fri: 8AM-8PM<br />Sat-Sun: 9AM-5PM</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <div className="border-top pt-3 mt-4">
                  <p className="text-muted small mb-3">
                    These Terms of Service for our FTTH services were last updated on {lastUpdated}. 
                    By using our services, you agree to these terms. We reserve the right to update these terms 
                    with proper notice to our customers.
                  </p>
                  <div className="d-flex flex-wrap gap-2">
                    <Link to="/privacy" className="btn btn-outline-primary btn-sm">
                      <i className="fas fa-shield-alt me-1"></i>
                      Privacy Policy
                    </Link>
                    <Link to="/" className="btn btn-link btn-sm text-secondary">
                      <i className="fas fa-arrow-left me-1"></i>
                      Back to Home
                    </Link>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default TermsOfService;
